import {useState} from 'react';
import {FavoriteDoctorsProfessional, FavoriteDoctorsResponse} from 'domain/usecases';
import {useHealthSpecialtiesSelection} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthSpecialtySelection/components/HealthSpecialtiesList/hooks/useHealthSpecialtiesSelection';
import {Specialty} from 'presentation/utils';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from '../../shared/store/useBookingStore';

const useProfessionalScheduling = (doctors: FavoriteDoctorsResponse | undefined) => {
	const [selectedProfessional, setSelectedProfessional] =
		useState<FavoriteDoctorsProfessional | null>(null);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [openUnaccreditedProvidersDrawer, setOpenUnaccreditedProvidersDrawer] = useState(false);

	const handleOpenUnaccreditedProvidersDrawer = () => {
		setOpenUnaccreditedProvidersDrawer(true);
	};

	const handleCloseUnaccreditedProvidersDrawer = () => {
		setOpenUnaccreditedProvidersDrawer(false);
	};

	const {PROFESSIONAL_STEP} = getHealthStepPosition({
		isManualLocation: false,
	});

	const handleNext = () => {
		setActiveBookingFlowStep(PROFESSIONAL_STEP);
	};

	const {
		handleSelectCard,
		checkBookingModal,
		setCheckBookingModal,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
	} = useHealthSpecialtiesSelection({
		handleNextStep: handleNext,
	});

	const selectedProfessionalData = doctors?.especialidades
		.flatMap(especialidade => especialidade.profissionais)
		.find(
			profissional =>
				profissional.cdPrestadorFisico.toString() ===
				selectedProfessional?.cdPrestadorFisico.toString(),
		);

	const getSelectedAddress = () => {
		const address = selectedProfessionalData?.estabelecimentos?.[0]?.endereco;
		return {
			cityName: address?.cidade || '',
			ufCode: address?.estado || '',
			ufName: address?.estado || '',
		};
	};

	const handleScheduleAppointment = async ({
		professional,
	}: {
		professional: FavoriteDoctorsProfessional;
	}) => {
		setSelectedProfessional(professional);

		setBookingData(prev => ({
			...prev,
			professionalCode: professional.cdPrestadorFisico.toString(),
			professionalName: professional.nmPrestadorFisico,
			isFavoriteDoctorsFlow: true,
			isRebookingFlow: false,
		}));

		const cityName = professional.estabelecimentos?.[0]?.endereco?.cidade;
		const ufCode = professional.estabelecimentos?.[0]?.endereco?.estado;

		const concatenatedSpecialty = professional.nmEspecialidade.concat(
			professional.nmSubEspecialidade ? ` - ${professional.nmSubEspecialidade}` : '',
		);

		const specialty: Specialty = {
			code: professional.cdEspecialidade.toString(),
			sub_code: professional?.cdSubEspecialidade
				? professional.cdSubEspecialidade.toString()
				: '',
			description: concatenatedSpecialty,
			formattedText: concatenatedSpecialty,
			cityName,
			ufCode,
			isFavoriteDoctorsFlow: true,
			telemedicine: '',
		};

		handleSelectCard(specialty);
	};

	const handleResetProfessionalSelection = () => {
		setSelectedProfessional(null);
	};

	return {
		handleScheduleAppointment,
		getSelectedAddress,
		checkBookingModal,
		setCheckBookingModal,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
		selectedProfessional,
		handleResetProfessionalSelection,
		openUnaccreditedProvidersDrawer,
		handleOpenUnaccreditedProvidersDrawer,
		handleCloseUnaccreditedProvidersDrawer,
	};
};

export {useProfessionalScheduling};
